import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { Users, Plus, Minus } from "lucide-react";
import { cn } from "../../lib/utils";

export interface GuestsSelectorProps {
  adults: number;
  childrenCount: number;
  infants: number;
  pets: number;
  onGuestsChange?: (guests: {
    adults: number;
    children: number;
    infants: number;
    pets: number;
  }) => void;
  disabled?: boolean;
  textColor?: string;
  className?: string;
}

const GuestsSelector = React.forwardRef<HTMLButtonElement, GuestsSelectorProps>(
  (
    {
      adults,
      childrenCount,
      infants,
      pets,
      onGuestsChange,
      disabled = false,
      textColor,
      className,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [tempGuests, setTempGuests] = React.useState({
      adults,
      children: childrenCount,
      infants,
      pets,
    });

    React.useEffect(() => {
      setTempGuests({ adults, children: childrenCount, infants, pets });
    }, [adults, childrenCount, infants, pets]);

    const totalGuests =
      tempGuests.adults +
      tempGuests.children +
      tempGuests.infants +
      tempGuests.pets;

    const updateGuestCount = (type: keyof typeof tempGuests, delta: number) => {
      setTempGuests((prev) => {
        const newValue = Math.max(0, prev[type] + delta);
        return { ...prev, [type]: newValue };
      });
    };

    const handleSave = () => {
      onGuestsChange?.(tempGuests);
      setOpen(false);
    };

    const handleCancel = () => {
      setTempGuests({ adults, children: childrenCount, infants, pets });
      setOpen(false);
    };

    const formatGuestsText = () => {
      if (totalGuests === 0) return "Add guests";
      if (totalGuests === 1) return "1 guest";
      return `${totalGuests} guests`;
    };

    return (
      <div className="space-y-1">
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>
            <button
              ref={ref}
              type="button"
              disabled={disabled}
              className={cn(
                "flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50",
                className,
              )}
              {...props}
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "text-sm font-medium",
                    !textColor && "text-gray-500",
                  )}
                  style={textColor ? { color: textColor } : undefined}
                >
                  {formatGuestsText()}
                </span>
              </div>
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              className="z-50 w-80 rounded-md border border-gray-200 bg-white p-4 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
              sideOffset={4}
            >
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">Guests</h3>

                {/* Adults */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Adults
                      </p>
                      <p className="text-xs text-gray-500">Ages 13 and above</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateGuestCount("adults", -1)}
                        disabled={tempGuests.adults <= 1}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-8 text-center text-sm font-medium">
                        {tempGuests.adults}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateGuestCount("adults", 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Children */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Children
                      </p>
                      <p className="text-xs text-gray-500">Ages 2-12</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateGuestCount("children", -1)}
                        disabled={tempGuests.children <= 0}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-8 text-center text-sm font-medium">
                        {tempGuests.children}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateGuestCount("children", 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Infants */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Infants
                      </p>
                      <p className="text-xs text-gray-500">Under 2</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateGuestCount("infants", -1)}
                        disabled={tempGuests.infants <= 0}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-8 text-center text-sm font-medium">
                        {tempGuests.infants}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateGuestCount("infants", 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Pets */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Pets</p>
                      <p className="text-xs text-gray-500">
                        Are you bringing pets?
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateGuestCount("pets", -1)}
                        disabled={tempGuests.pets <= 0}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-8 text-center text-sm font-medium">
                        {tempGuests.pets}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateGuestCount("pets", 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Save
                  </button>
                </div>
              </div>
              <Popover.Arrow className="fill-white" />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
    );
  },
);

GuestsSelector.displayName = "GuestsSelector";

export { GuestsSelector };
